import * as Sentry from '@sentry/react';
import { useEffect } from 'react';

import { ENVIRONMENT } from 'src/settings';

/**
 * Loads the Meta (Facebook) Pixel and fires a PageView event.
 * Only runs in the production environment.
 *
 * Errors are swallowed and reported as Sentry warnings — fbevents.js can throw
 * when accessing window.webkit.messageHandlers in iOS WebView contexts where
 * the native bridge is absent.
 */
const useMetaPixel = (pixelId: string) => {
  useEffect(() => {
    if (ENVIRONMENT !== 'production') return;

    let script: HTMLScriptElement | null = null;
    let noscript: HTMLElement | null = null;

    try {
      /* eslint-disable */
      const w: any = window;
      if (w.fbq) return;

      const n: any = (w.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      });
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      /* eslint-enable */

      script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);

      (window as any).fbq('init', pixelId);

      (window as any).fbq('track', 'PageView');

      noscript = document.createElement('noscript');
      const img = document.createElement('img');

      img.height = 1;
      img.width = 1;
      img.style.display = 'none';
      img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
      noscript.appendChild(img);
      document.body.appendChild(noscript);
    } catch (error) {
      Sentry.captureMessage('useMetaPixel: pixel initialisation failed', {
        level: 'warning',
        extra: { error, pixelId }
      });
    }

    return () => {
      if (script && document.head.contains(script)) {
        document.head.removeChild(script);
      }
      if (noscript && document.body.contains(noscript)) {
        document.body.removeChild(noscript);
      }
    };
  }, [pixelId]);
};

export default useMetaPixel;
